import React, { useState, useEffect, useCallback, useRef, useMemo } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import AdminLayout from "../../Layout/AdminLayout";
import API from "../../Api/Api";
import endpoints from "../../Api/Endpoints";
import {
  StarRatingDisplay,
  InteractiveStarRating,
  TickIcon,
  XIcon,
  TrashIcon,
} from "../../Components/AdminShared";
import { SkeletonBlock } from "../../Components/Skeleton/Skeleton";
import AvatarImage from "../../Components/AvatarImage/AvatarImage";
import "./NewSignUps.scss";
import { showAlert, showConfirm } from "../../feedback/feedbackApi";

const LIST_TYPE = { PATIENT: "patient", PROVIDER: "provider" };
const STATUS_FILTERS = [
  { value: "all", label: "All" },
  { value: "pending", label: "Pending" },
  { value: "approved", label: "Approved" },
  { value: "rejected", label: "Rejected" },
  { value: "blacklisted", label: "Blacklisted" },
];
const PATIENT_STATUS_FILTERS = [
  { value: "all", label: "All" },
];
const MOBILE_BREAKPOINT_PX = 1100;
const PAGE_SIZE_OPTIONS = [10, 25, 50, 100];
const DEFAULT_PAGE_SIZE = 25;

function normalizePageSize(value) {
  const n = Number(value);
  return PAGE_SIZE_OPTIONS.includes(n) ? n : DEFAULT_PAGE_SIZE;
}

/** Build page numbers with ellipsis for desktop pagination bar. */
function getVisiblePageNumbers(currentPage, totalPages) {
  const total = Math.max(0, Number(totalPages) || 0);
  const current = Math.min(Math.max(1, Number(currentPage) || 1), Math.max(1, total));
  if (total <= 0) return [];
  if (total <= 7) {
    return Array.from({ length: total }, (_, i) => i + 1);
  }
  const pages = new Set([1, total, current, current - 1, current + 1]);
  const sorted = [...pages].filter((p) => p >= 1 && p <= total).sort((a, b) => a - b);
  const out = [];
  for (let i = 0; i < sorted.length; i++) {
    if (i > 0 && sorted[i] - sorted[i - 1] > 1) out.push("ellipsis");
    out.push(sorted[i]);
  }
  return out;
}

/** e.g. "Showing 1 to 22 of 22 doctors" */
function formatShowingRange({ start, end, total, entityPlural, entitySingular }) {
  const label = total === 1 ? entitySingular : entityPlural;
  if (total === 0) return `No ${entityPlural}`;
  if (start === end) return `Showing ${start} of ${total} ${label}`;
  return `Showing ${start} to ${end} of ${total} ${label}`;
}

function getPageRange({ currentPage, pageSize, totalItems, displayedCount }) {
  const total = Math.max(0, Number(totalItems) || 0);
  if (total === 0) return { start: 0, end: 0 };
  const current = Math.max(1, Number(currentPage) || 1);
  const start = (current - 1) * pageSize + 1;
  const rowsOnPage = Math.max(0, Number(displayedCount) || 0);
  const end = Math.min(start + (rowsOnPage > 0 ? rowsOnPage - 1 : pageSize - 1), total);
  return { start, end: Math.max(start, end) };
}

function PageSizeSelect({ value, onChange, disabled, className = "" }) {
  return (
    <label className={`ns_page_size_field${className ? ` ${className}` : ""}`}>
      <span className="ns_page_size_label">Rows per page</span>
      <select
        className="ns_page_size_select"
        value={normalizePageSize(value)}
        disabled={disabled}
        aria-label="Rows per page"
        onChange={(e) => onChange(normalizePageSize(e.target.value))}
      >
        {PAGE_SIZE_OPTIONS.map((size) => (
          <option key={size} value={size}>
            {size}
          </option>
        ))}
      </select>
    </label>
  );
}

function ListPaginationBar({
  currentPage,
  totalPages,
  totalItems,
  pageSize,
  displayedCount,
  entityPlural,
  entitySingular,
  loading,
  onPageChange,
  onPageSizeChange,
}) {
  const total = Math.max(0, Number(totalItems) || 0);
  const pages = Math.max(1, Number(totalPages) || 1);
  const current = Math.min(Math.max(1, Number(currentPage) || 1), pages);
  const { start, end } = getPageRange({
    currentPage: current,
    pageSize,
    totalItems: total,
    displayedCount,
  });
  const pageNums = getVisiblePageNumbers(current, pages);

  return (
    <div className="ns_pagination ns_pagination--desktop" role="navigation" aria-label="Pagination">
      <p className="ns_pagination_info">
        {loading && total === 0
          ? "Loading…"
          : formatShowingRange({ start, end, total, entityPlural, entitySingular })}
      </p>
      <div className="ns_pagination_controls">
        <PageSizeSelect
          value={pageSize}
          disabled={loading}
          onChange={onPageSizeChange}
        />
        <button
          type="button"
          className="ns_page_btn"
          disabled={loading || current <= 1}
          onClick={() => onPageChange(current - 1)}
        >
          Previous
        </button>
        <div className="ns_page_nums" role="group" aria-label="Page numbers">
          {pageNums.map((item, idx) =>
            item === "ellipsis" ? (
              <span key={`ellipsis-${idx}`} className="ns_page_ellipsis" aria-hidden>
                …
              </span>
            ) : (
              <button
                key={item}
                type="button"
                className={`ns_page_num_btn${item === current ? " active" : ""}`}
                disabled={loading || item === current}
                aria-current={item === current ? "page" : undefined}
                onClick={() => onPageChange(item)}
              >
                {item}
              </button>
            )
          )}
        </div>
        <button
          type="button"
          className="ns_page_btn"
          disabled={loading || current >= pages}
          onClick={() => onPageChange(current + 1)}
        >
          Next
        </button>
      </div>
    </div>
  );
}

function formatDate(v) {
  if (!v) return "—";
  try {
    const d = typeof v === "string" ? new Date(v) : v;
    return isNaN(d.getTime()) ? "—" : d.toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" });
  } catch {
    return "—";
  }
}

/** Provider list rating cell: one decimal or em dash when unset. */
function formatRatingScore(value) {
  if (value == null || value === "") return "—";
  const n = Number(value);
  if (Number.isNaN(n)) return "—";
  return n.toFixed(1);
}

/** Admin patient list: empty or placeholder names (mid-registration, legacy "-") show as Unregistered, not a dash. */
function getPatientDisplayName(row) {
  if (!row || typeof row !== "object") return "Unregistered";
  const raw = String(row.patientName ?? row.name ?? "").trim();
  if (!raw || raw === "-" || raw === "—" || raw === "–") return "Unregistered";
  return raw;
}

export default function NewSignUps() {
  const location = useLocation();
  const navigate = useNavigate();
  const pathname = location.pathname;
  const isPatientListRoute = pathname === "/patient-list";
  const isProviderListRoute = pathname === "/provider-list";
  const isDoctorListRoute = pathname === "/doctor-list";
  const isHospitalListRoute = pathname === "/hospital-list";
  const isSplitListRoute =
    isPatientListRoute || isProviderListRoute || isDoctorListRoute || isHospitalListRoute;
  const providerRouteScope = isDoctorListRoute ? "doctor" : isHospitalListRoute ? "hospital" : "all";
  const [listType, setListType] = useState(() => {
    if (isProviderListRoute || isDoctorListRoute || isHospitalListRoute) return LIST_TYPE.PROVIDER;
    return LIST_TYPE.PATIENT;
  });
  const [list, setList] = useState([]);
  const [patients, setPatients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState("all");
  const [platformRateModal, setPlatformRateModal] = useState(null);
  const [rating, setRating] = useState({});
  const [actionLoading, setActionLoading] = useState({ approve: null, reject: null, rate: null, restrict: null, delete: null });
  const [restrictModal, setRestrictModal] = useState(null);
  const [restrictDays, setRestrictDays] = useState(7);
  const [restrictReason, setRestrictReason] = useState("");
  const [rejectModal, setRejectModal] = useState(null);
  const [rejectionReason, setRejectionReason] = useState("");
  const [patientStatusFilter, setPatientStatusFilter] = useState("all");
  const [searchHighlightProvider, setSearchHighlightProvider] = useState(null);
  const [searchHighlightPatient, setSearchHighlightPatient] = useState(null);
  const [searchInput, setSearchInput] = useState("");
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState("");
  const [mobileFilterOpen, setMobileFilterOpen] = useState(false);
  const [patientDesktopPage, setPatientDesktopPage] = useState(1);
  const [providerDesktopPage, setProviderDesktopPage] = useState(1);
  const [patientTotal, setPatientTotal] = useState(0);
  const [providerTotal, setProviderTotal] = useState(0);
  const [patientHasMore, setPatientHasMore] = useState(true);
  const [providerHasMore, setProviderHasMore] = useState(true);
  const [providerPageSize, setProviderPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [patientPageSize, setPatientPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [loadingMore, setLoadingMore] = useState(false);
  const [isMobilePagination, setIsMobilePagination] = useState(() => {
    try {
      return window.matchMedia(`(max-width: ${MOBILE_BREAKPOINT_PX}px)`).matches;
    } catch {
      return false;
    }
  });
  const [searchParams, setSearchParams] = useSearchParams();
  const highlightId = searchParams.get("highlight") || null;
  const tableWrapRef = useRef(null);
  const tableScrollRef = useRef(null);
  const bodySearchInputRef = useRef(null);
  const triedHighlightIdRef = useRef(null);
  const loadingMoreRef = useRef(false);
  const patientNextPageRef = useRef(1);
  const providerNextPageRef = useRef(1);
  const [bodySearchOpen, setBodySearchOpen] = useState(false);

  useEffect(() => {
    try {
      const mq = window.matchMedia(`(max-width: ${MOBILE_BREAKPOINT_PX}px)`);
      const sync = () => setIsMobilePagination(mq.matches);
      sync();
      mq.addEventListener("change", sync);
      return () => mq.removeEventListener("change", sync);
    } catch {
      return undefined;
    }
  }, []);

  const mediaPaginationSyncedRef = useRef(false);
  useEffect(() => {
    if (!mediaPaginationSyncedRef.current) {
      mediaPaginationSyncedRef.current = true;
      return;
    }
    if (highlightId) return;
    if (effectiveListType === LIST_TYPE.PROVIDER) {
      loadingMoreRef.current = false;
      setProviderDesktopPage(1);
      providerNextPageRef.current = 1;
      setProviderHasMore(true);
      fetchProviders({ page: 1, append: false });
      return;
    }
    if (effectiveListType === LIST_TYPE.PATIENT) {
      loadingMoreRef.current = false;
      setPatientDesktopPage(1);
      patientNextPageRef.current = 1;
      setPatientHasMore(true);
      fetchPatients({ page: 1, append: false });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- refetch only when layout mode changes
  }, [isMobilePagination]);

  /** Pathname wins on split routes so fetch/UI never lag one render behind `listType` (fixes filters + wrong list after navigate). */
  const effectiveListType = useMemo(() => {
    if (isPatientListRoute) return LIST_TYPE.PATIENT;
    if (isProviderListRoute || isDoctorListRoute || isHospitalListRoute) return LIST_TYPE.PROVIDER;
    return listType;
  }, [isPatientListRoute, isProviderListRoute, isDoctorListRoute, isHospitalListRoute, listType]);

  const getPatientId = useCallback((row) => {
    return row?.patientId || row?._id || row?.id || null;
  }, []);

  useEffect(() => {
    const timer = window.setTimeout(() => {
      setDebouncedSearchQuery(searchInput.trim());
    }, 300);
    return () => window.clearTimeout(timer);
  }, [searchInput]);

  useEffect(() => {
    const isMobileViewport = () => {
      try {
        return window.matchMedia("(max-width: 1100px)").matches;
      } catch (_) {
        return false;
      }
    };
    const handleToggleSearch = (event) => {
      const forceOpen = event?.detail?.open;
      if (!isMobileViewport()) return;
      setBodySearchOpen((prev) => (typeof forceOpen === "boolean" ? forceOpen : !prev));
    };
    const handleSearchChange = (event) => {
      setSearchInput(String(event?.detail || ""));
    };
    const handleOpenFilter = () => {
      setMobileFilterOpen(true);
    };

    window.addEventListener("admin:new-signups-toggle-search", handleToggleSearch);
    window.addEventListener("admin:new-signups-search-change", handleSearchChange);
    window.addEventListener("admin:new-signups-open-filter", handleOpenFilter);
    return () => {
      window.removeEventListener("admin:new-signups-toggle-search", handleToggleSearch);
      window.removeEventListener("admin:new-signups-search-change", handleSearchChange);
      window.removeEventListener("admin:new-signups-open-filter", handleOpenFilter);
    };
  }, []);

  useEffect(() => {
    if (!bodySearchOpen) return;
    bodySearchInputRef.current?.focus();
  }, [bodySearchOpen]);

  const fetchProviders = useCallback(
    async ({ page = 1, append = false, limit: limitOverride } = {}) => {
      if (append) {
        if (loadingMoreRef.current) return;
        loadingMoreRef.current = true;
        setLoadingMore(true);
      } else {
        setLoading(true);
      }
      const limit = normalizePageSize(limitOverride ?? providerPageSize);
      try {
        const params = { page, limit };
        if (statusFilter !== "all") params.status = statusFilter;
        if (debouncedSearchQuery) params.q = debouncedSearchQuery;
        const shouldLoadDoctors = providerRouteScope !== "hospital";
        const shouldLoadHospitals = providerRouteScope !== "doctor";
        const [docRes, hospRes] = await Promise.all([
          shouldLoadDoctors
            ? API.get(endpoints.ADMIN_DOCTORS_LIST, { params })
            : Promise.resolve({ data: { data: [], pagination: { total: 0 } } }),
          shouldLoadHospitals
            ? API.get(endpoints.ADMIN_HOSPITALS_LIST, { params })
            : Promise.resolve({ data: { data: [], pagination: { total: 0 } } }),
        ]);
        const doctors = (docRes?.data?.data ?? docRes?.data ?? []).map((d) => ({
          id: d.doctorId || d._id,
          type: "doctor",
          name: d.doctorName || d.name || "—",
          image: d.image || "",
          createdAt: d.createdAt,
          dateCreated: d.dateCreated || formatDate(d.createdAt),
          dateCreatedFormatted: d.dateCreatedFormatted,
          status:
            d.status === "approved"
              ? "Approved"
              : d.status === "pending_admin" || d.status === "pending_selection"
                ? "Pending"
                : d.status || "Pending",
          adminRating: d.adminRating ?? null,
          patientRatingAverage: d.patientRatingAverage ?? null,
          patientRatingCount: d.patientRatingCount ?? 0,
          isBlacklisted: d.isBlacklisted || false,
          restrictedUntil: d.restrictedUntil || null,
        }));
        const hospitals = (hospRes?.data?.data ?? hospRes?.data ?? []).map((h) => ({
          id: h.hospitalId || h._id,
          type: "hospital",
          name: h.hospitalName || h.name || "—",
          image: h.avatar || h.image || "",
          createdAt: h.createdAt,
          dateCreated: h.dateCreated || formatDate(h.createdAt),
          dateCreatedFormatted: h.dateCreatedFormatted,
          status:
            h.status === "approved"
              ? "Approved"
              : h.status === "pending_admin" || h.status === "pending_selection"
                ? "Pending"
                : h.status || "Pending",
          adminRating: h.adminRating ?? null,
          patientRatingAverage: h.patientRatingAverage ?? null,
          patientRatingCount: h.patientRatingCount ?? 0,
          isBlacklisted: h.isBlacklisted || false,
          restrictedUntil: h.restrictedUntil || null,
        }));
        const merged = [...doctors, ...hospitals];
        const docTotal =
          Number(docRes?.data?.pagination?.total) ||
          Number(docRes?.data?.total) ||
          doctors.length;
        const hospTotal =
          Number(hospRes?.data?.pagination?.total) ||
          Number(hospRes?.data?.total) ||
          hospitals.length;
        const total =
          shouldLoadDoctors && shouldLoadHospitals
            ? docTotal + hospTotal
            : shouldLoadDoctors
              ? docTotal
              : hospTotal;
        setProviderTotal(total);

        setList((prev) => {
          const next = append
            ? [...prev, ...merged.filter((item) => !prev.some((p) => p.id === item.id && p.type === item.type))]
            : merged;
          setProviderHasMore(next.length < total && merged.length > 0);
          return next;
        });

        if (append) {
          providerNextPageRef.current = page + 1;
        } else {
          setProviderDesktopPage(page);
          providerNextPageRef.current = page + 1;
        }
      } catch (err) {
        console.error(err);
        if (!append) setList([]);
        setProviderHasMore(false);
        setProviderTotal(0);
      } finally {
        if (!append) setLoading(false);
        setLoadingMore(false);
        loadingMoreRef.current = false;
      }
    },
    [statusFilter, debouncedSearchQuery, providerRouteScope, providerPageSize]
  );

  const fetchPatients = useCallback(
    async ({ page = 1, append = false, limit: limitOverride } = {}) => {
      if (append) {
        if (loadingMoreRef.current) return;
        loadingMoreRef.current = true;
        setLoadingMore(true);
      } else {
        setLoading(true);
      }
      const limit = normalizePageSize(limitOverride ?? patientPageSize);
      try {
        const params = { page, limit };
        if (patientStatusFilter !== "all") params.status = patientStatusFilter;
        if (debouncedSearchQuery) params.q = debouncedSearchQuery;
        const res = await API.get(endpoints.ADMIN_PATIENTS, { params });
        const data = res?.data?.data ?? res?.data ?? [];
        const total =
          Number(res?.data?.pagination?.total) ||
          Number(res?.data?.total) ||
          data.length;
        const sorted = Array.isArray(data) ? data : [];
        setPatientTotal(total);

        setPatients((prev) => {
          const next = append
            ? [
                ...prev,
                ...sorted.filter((item) => {
                  const id = String(item?.patientId || item?._id || item?.id || "");
                  if (!id) return false;
                  return !prev.some((p) => String(p?.patientId || p?._id || p?.id || "") === id);
                }),
              ]
            : sorted;
          setPatientHasMore(next.length < total && sorted.length > 0);
          return next;
        });

        if (append) {
          patientNextPageRef.current = page + 1;
        } else {
          setPatientDesktopPage(page);
          patientNextPageRef.current = page + 1;
        }
      } catch (err) {
        console.error(err);
        if (!append) setPatients([]);
        setPatientHasMore(false);
        setPatientTotal(0);
      } finally {
        if (!append) setLoading(false);
        setLoadingMore(false);
        loadingMoreRef.current = false;
      }
    },
    [patientStatusFilter, debouncedSearchQuery, patientPageSize]
  );

  useEffect(() => {
    if (isPatientListRoute) {
      setListType(LIST_TYPE.PATIENT);
      return;
    }
    if (isProviderListRoute || isDoctorListRoute || isHospitalListRoute) {
      setListType(LIST_TYPE.PROVIDER);
      return;
    }
    const tab = searchParams.get("tab");
    if (tab === "patient") setListType(LIST_TYPE.PATIENT);
    else if (tab === "provider") setListType(LIST_TYPE.PROVIDER);
  }, [searchParams, isPatientListRoute, isProviderListRoute, isDoctorListRoute, isHospitalListRoute]);

  useEffect(() => {
    if (effectiveListType === LIST_TYPE.PROVIDER) {
      loadingMoreRef.current = false;
      setProviderDesktopPage(1);
      providerNextPageRef.current = 1;
      setProviderHasMore(true);
      fetchProviders({ page: 1, append: false });
      return;
    }
    if (highlightId && highlightId.startsWith("patient-")) {
      setLoading(true);
      const params = { page: 1, limit: 100 };
      if (patientStatusFilter !== "all") params.status = patientStatusFilter;
      API.get(endpoints.ADMIN_PATIENTS, { params })
        .then((res) => {
          const data = Array.isArray(res?.data?.data ?? res?.data) ? (res?.data?.data ?? res?.data) : [];
          setPatients(data);
          const total =
            Number(res?.data?.pagination?.total) ||
            Number(res?.data?.total) ||
            data.length;
          setPatientTotal(total);
        })
        .catch(() => {
          setPatients([]);
          setPatientTotal(0);
        })
        .finally(() => setLoading(false));
      return;
    }
    loadingMoreRef.current = false;
    setPatientDesktopPage(1);
    patientNextPageRef.current = 1;
    setPatientHasMore(true);
    fetchPatients({ page: 1, append: false });
    // fetchPatients/fetchProviders omitted: page changes must not retrigger this reset effect.
    // eslint-disable-next-line react-hooks/exhaustive-deps -- intentional; see above
  }, [
    effectiveListType,
    highlightId,
    patientStatusFilter,
    statusFilter,
    debouncedSearchQuery,
    providerRouteScope,
    providerPageSize,
    patientPageSize,
  ]);

  useEffect(() => {
    if (!isMobilePagination || highlightId) return;
    const node = tableScrollRef.current;
    if (!node) return;

    const onScroll = () => {
      if (loading || loadingMore) return;
      const nearBottom = node.scrollTop + node.clientHeight >= node.scrollHeight - 120;
      if (!nearBottom) return;

      if (effectiveListType === LIST_TYPE.PATIENT && patientHasMore) {
        fetchPatients({ page: patientNextPageRef.current, append: true });
        return;
      }
      if (effectiveListType === LIST_TYPE.PROVIDER && providerHasMore) {
        fetchProviders({ page: providerNextPageRef.current, append: true });
      }
    };

    node.addEventListener("scroll", onScroll);
    return () => node.removeEventListener("scroll", onScroll);
  }, [
    isMobilePagination,
    effectiveListType,
    loading,
    loadingMore,
    highlightId,
    patientHasMore,
    providerHasMore,
    fetchPatients,
    fetchProviders,
  ]);

  const goToPatientPage = useCallback(
    (page) => {
      const p = Math.max(1, page);
      tableScrollRef.current?.scrollTo({ top: 0, behavior: "smooth" });
      patientNextPageRef.current = 1;
      fetchPatients({ page: p, append: false });
    },
    [fetchPatients]
  );

  const goToProviderPage = useCallback(
    (page) => {
      const p = Math.max(1, page);
      tableScrollRef.current?.scrollTo({ top: 0, behavior: "smooth" });
      providerNextPageRef.current = 1;
      fetchProviders({ page: p, append: false });
    },
    [fetchProviders]
  );

  const providerTotalPages = Math.max(1, Math.ceil(providerTotal / providerPageSize) || 1);
  const patientTotalPages = Math.max(1, Math.ceil(patientTotal / patientPageSize) || 1);

  const handleProviderPageSizeChange = useCallback((nextSize) => {
    setProviderPageSize(normalizePageSize(nextSize));
    setProviderDesktopPage(1);
    providerNextPageRef.current = 1;
    setProviderHasMore(true);
    tableScrollRef.current?.scrollTo({ top: 0, behavior: "smooth" });
  }, []);

  const handlePatientPageSizeChange = useCallback((nextSize) => {
    setPatientPageSize(normalizePageSize(nextSize));
    setPatientDesktopPage(1);
    patientNextPageRef.current = 1;
    setPatientHasMore(true);
    tableScrollRef.current?.scrollTo({ top: 0, behavior: "smooth" });
  }, []);

  useEffect(() => {
    if (!highlightId) {
      setSearchHighlightProvider(null);
      setSearchHighlightPatient(null);
      triedHighlightIdRef.current = null;
    }
  }, [highlightId]);

  useEffect(() => {
    if (!highlightId || effectiveListType !== LIST_TYPE.PROVIDER) return;
    const match = highlightId.match(/^(doctor|hospital)-(.+)$/);
    if (!match) return;
    const [, type, id] = match;
    const inList = list.some((r) => `${r.type}-${r.id}` === highlightId);
    if (inList) return;
    if (type === "doctor") {
      API.get(endpoints.adminDoctorById(id))
        .then((res) => {
          const d = res?.data?.data ?? res?.data;
          if (d) {
            const docId = d._id ?? d.doctorId ?? d.id;
            setSearchHighlightProvider({
              id: docId != null ? String(docId) : "",
              type: "doctor",
              name: d.name || "—",
              image: d.image || "",
              dateCreated: formatDate(d.createdAt),
              status:
                d.status === "approved"
                  ? "Approved"
                  : d.status === "pending_admin" || d.status === "pending_selection"
                    ? "Pending"
                    : d.status || "Pending",
              adminRating: d.adminRating ?? null,
              patientRatingAverage: d.patientRatingAverage ?? null,
              patientRatingCount: d.patientRatingCount ?? 0,
              isBlacklisted: !!d.isBlacklisted,
              restrictedUntil: d.restrictedUntil || null,
            });
          }
        })
        .catch(() => setSearchHighlightProvider(null));
    } else {
      API.get(endpoints.adminHospitalById(id))
        .then((res) => {
          const h = res?.data?.data ?? res?.data;
          if (h) {
            const hospId = h._id ?? h.hospitalId ?? h.id;
            setSearchHighlightProvider({
              id: hospId != null ? String(hospId) : "",
              type: "hospital",
              name: h.name || h.hospitalName || "—",
              image: h.avatar || h.image || "",
              dateCreated: formatDate(h.createdAt),
              status:
                h.status === "approved"
                  ? "Approved"
                  : h.status === "pending_admin" || h.status === "pending_selection"
                    ? "Pending"
                    : h.status || "Pending",
              adminRating: h.adminRating ?? null,
              patientRatingAverage: h.patientRatingAverage ?? null,
              patientRatingCount: h.patientRatingCount ?? 0,
              isBlacklisted: !!h.isBlacklisted,
              restrictedUntil: h.restrictedUntil || null,
            });
          }
        })
        .catch(() => setSearchHighlightProvider(null));
    }
  }, [highlightId, effectiveListType, list]);

  useEffect(() => {
    if (!highlightId || effectiveListType !== LIST_TYPE.PATIENT || !highlightId.startsWith("patient-")) return;
    const highlightPatientId = highlightId.slice("patient-".length);
    const inList = patients.some((p) => String(p.patientId ?? p._id) === highlightPatientId);
    if (inList) {
      setSearchHighlightPatient(null);
      return;
    }
    if (triedHighlightIdRef.current === highlightId) return;
    triedHighlightIdRef.current = highlightId;
    API.get(endpoints.adminPatientById(highlightPatientId))
      .then((res) => {
        const data = res?.data?.data ?? res?.data;
        if (data) setSearchHighlightPatient(data);
      })
      .catch(() => setSearchHighlightPatient(null));
  }, [highlightId, effectiveListType, patients, patientStatusFilter]);

  useEffect(() => {
    if (!highlightId) return;
    let removeHighlightId;
    const timeoutId = setTimeout(() => {
      const el = tableWrapRef.current?.querySelector(`[data-row-id="${highlightId}"]`);
      if (el) {
        el.scrollIntoView({ behavior: "smooth", block: "center" });
        el.classList.add("row_highlight");
        removeHighlightId = setTimeout(() => el.classList.remove("row_highlight"), 4000);
      }
    }, 150);
    return () => {
      clearTimeout(timeoutId);
      if (removeHighlightId) clearTimeout(removeHighlightId);
    };
  }, [highlightId, loading, effectiveListType, list.length, patients.length, searchHighlightProvider, searchHighlightPatient]);


  const goToProviderDetail = (row) => {
    const path = row.type === "hospital" ? `/hospital-list/${row.id}` : `/doctor-list/${row.id}`;
    navigate(path);
  };

  const approve = async (id, type) => {
    setActionLoading((p) => ({ ...p, approve: id }));
    try {
      const url = type === "doctor" ? endpoints.adminDoctorApprove(id) : endpoints.adminHospitalApprove(id);
      await API.post(url);
      loadingMoreRef.current = false;
      setProviderDesktopPage(1);
      providerNextPageRef.current = 1;
      setProviderHasMore(true);
      fetchProviders({ page: 1, append: false });
    } catch (err) {
      showAlert(err?.response?.data?.message || "Failed to approve", {
        variant: "error",
        title: "Approve failed",
      });
    } finally {
      setActionLoading((p) => ({ ...p, approve: null }));
    }
  };

  const reject = async (id, type) => {
    try {
      const reason = String(rejectionReason || "").trim();
      if (!reason) {
        showAlert("Rejection reason is required.", { variant: "warning", title: "Reason required" });
        return;
      }
      setActionLoading((p) => ({ ...p, reject: id }));
      const url = type === "doctor" ? endpoints.adminDoctorReject(id) : endpoints.adminHospitalReject(id);
      await API.post(url, { rejectionReason: reason });
      setRejectModal(null);
      setRejectionReason("");
      loadingMoreRef.current = false;
      setProviderDesktopPage(1);
      providerNextPageRef.current = 1;
      setProviderHasMore(true);
      fetchProviders({ page: 1, append: false });
    } catch (err) {
      showAlert(err?.response?.data?.message || "Failed to reject", {
        variant: "error",
        title: "Reject failed",
      });
    } finally {
      setActionLoading((p) => ({ ...p, reject: null }));
    }
  };

  const submitRejectModal = () => {
    if (!rejectModal) return;
    reject(rejectModal.id, rejectModal.type);
  };

  const setRate = async (id, type, value) => {
    setPlatformRateModal(null);
    setActionLoading((p) => ({ ...p, rate: id }));
    try {
      const url = type === "doctor" ? endpoints.adminDoctorRating(id) : endpoints.adminHospitalRating(id);
      await API.put(url, { rating: value });
      setRating((r) => ({ ...r, [id]: value }));
      loadingMoreRef.current = false;
      setProviderDesktopPage(1);
      providerNextPageRef.current = 1;
      setProviderHasMore(true);
      fetchProviders({ page: 1, append: false });
    } catch (err) {
      showAlert(err?.response?.data?.message || "Failed to set rating", {
        variant: "error",
        title: "Rating not saved",
      });
    } finally {
      setActionLoading((p) => ({ ...p, rate: null }));
    }
  };

  const restrict = async (row, type, days) => {
    const reason = restrictReason.trim();
    if (!reason) {
      showAlert("Please enter a reason for restriction.", { variant: "warning", title: "Reason required" });
      return;
    }
    setActionLoading((p) => ({ ...p, restrict: row.id }));
    try {
      const url = row.type === "doctor" ? endpoints.adminDoctorRestrict(row.id) : endpoints.adminHospitalRestrict(row.id);
      const body = {
        type: type === "lifetime" ? "lifetime" : "days",
        days: type === "days" ? days : undefined,
        reason,
      };
      await API.patch(url, body);
      setRestrictModal(null);
      setRestrictReason("");
      loadingMoreRef.current = false;
      setProviderDesktopPage(1);
      providerNextPageRef.current = 1;
      setProviderHasMore(true);
      fetchProviders({ page: 1, append: false });
    } catch (err) {
      showAlert(err?.response?.data?.message || "Failed to restrict", {
        variant: "error",
        title: "Restrict failed",
      });
    } finally {
      setActionLoading((p) => ({ ...p, restrict: null }));
    }
  };

  const unrestrict = async (row) => {
    setActionLoading((p) => ({ ...p, restrict: row.id }));
    try {
      const url = row.type === "doctor" ? endpoints.adminDoctorUnrestrict(row.id) : endpoints.adminHospitalUnrestrict(row.id);
      await API.patch(url);
      setRestrictModal(null);
      loadingMoreRef.current = false;
      setProviderDesktopPage(1);
      providerNextPageRef.current = 1;
      setProviderHasMore(true);
      fetchProviders({ page: 1, append: false });
    } catch (err) {
      showAlert(err?.response?.data?.message || "Failed to remove restriction", {
        variant: "error",
        title: "Update failed",
      });
    } finally {
      setActionLoading((p) => ({ ...p, restrict: null }));
    }
  };

  const deleteProvider = async (id, type) => {
    const label = type === "doctor" ? "doctor" : "hospital";
    const ok = await showConfirm(`Remove this ${label} from the platform? This cannot be undone.`, {
      title: "Remove from platform",
      confirmText: "Remove",
      danger: true,
    });
    if (!ok) return;
    setActionLoading((p) => ({ ...p, delete: id }));
    try {
      const url = type === "doctor" ? endpoints.adminDoctorDelete(id) : endpoints.adminHospitalDelete(id);
      await API.delete(url);
      setList((prev) => prev.filter((row) => !(String(row.id) === String(id) && row.type === type)));
      setSearchHighlightProvider((prev) =>
        prev && String(prev.id) === String(id) && prev.type === type ? null : prev
      );
      if (highlightId === `${type}-${id}`) {
        setSearchParams((p) => {
          const next = new URLSearchParams(p);
          next.delete("highlight");
          return next;
        }, { replace: true });
      }
      loadingMoreRef.current = false;
      setProviderDesktopPage(1);
      providerNextPageRef.current = 1;
      setProviderHasMore(true);
      fetchProviders({ page: 1, append: false });
    } catch (err) {
      showAlert(err?.response?.data?.message || "Failed to delete record", {
        variant: "error",
        title: "Delete failed",
      });
    } finally {
      setActionLoading((p) => ({ ...p, delete: null }));
    }
  };

  const deletePatient = async (patientId) => {
    if (!patientId) return;
    const ok = await showConfirm("Remove this patient from the platform? This cannot be undone.", {
      title: "Remove patient",
      confirmText: "Remove",
      danger: true,
    });
    if (!ok) return;
    setActionLoading((p) => ({ ...p, delete: patientId }));
    try {
      await API.delete(endpoints.adminPatientDelete(patientId));
      showAlert("Patient was removed from the platform.", {
        variant: "success",
        title: "Removed",
        duration: 4200,
      });

      // If we were viewing via a search highlight, clear it so UI doesn't show a deleted row.
      if (highlightId === `patient-${patientId}`) {
        setSearchParams((p) => {
          const next = new URLSearchParams(p);
          next.delete("highlight");
          return next;
        }, { replace: true });
      }
      setPatients((prev) =>
        prev.filter((row) => String(getPatientId(row)) !== String(patientId))
      );
      setSearchHighlightPatient((prev) =>
        prev && String(getPatientId(prev)) === String(patientId) ? null : prev
      );
      loadingMoreRef.current = false;
      setPatientDesktopPage(1);
      patientNextPageRef.current = 1;
      setPatientHasMore(true);
      fetchPatients({ page: 1, append: false });
    } catch (err) {
      showAlert(err?.response?.data?.message || "Failed to delete patient", {
        variant: "error",
        title: "Delete failed",
      });
    } finally {
      setActionLoading((p) => ({ ...p, delete: null }));
    }
  };

  const isRestricted = (row) => row.isBlacklisted || (row.restrictedUntil && new Date(row.restrictedUntil) > new Date());

  const displayProviderList = highlightId
    ? (searchHighlightProvider ? [searchHighlightProvider] : list.filter((r) => `${r.type}-${r.id}` === highlightId))
    : list;
  const highlightPatientId = highlightId && highlightId.startsWith("patient-") ? highlightId.slice("patient-".length) : "";
  const displayPatientList = searchHighlightPatient
    ? [searchHighlightPatient]
    : highlightPatientId
      ? patients.filter((p) => String(p.patientId ?? p._id) === highlightPatientId)
      : patients;

  const getStatusDisplay = (row) => {
    if (row.isBlacklisted || (row.restrictedUntil && new Date(row.restrictedUntil) > new Date())) return { label: "Blacklisted", bullet: "blacklisted" };
    const rawStatus = String(row.status || "").trim();
    const approvalStatus = String(row.adminApprovalStatus || "").trim().toLowerCase();
    const s = rawStatus.toLowerCase();
    if (approvalStatus === "not_submitted" || s === "not_submitted" || s === "not submitted") {
      return { label: "Not submitted", bullet: "pending" };
    }
    if (!rawStatus || rawStatus === "—" || rawStatus === "-") return { label: "Pending", bullet: "pending" };
    if (s === "approved") return { label: "Approved", bullet: "approved" };
    if (s === "rejected") return { label: "Rejected", bullet: "rejected" };
    if (s === "pending" || s === "pending_admin" || s === "pending_selection") return { label: "Pending", bullet: "pending" };
    return { label: rawStatus, bullet: "pending" };
  };
  const providerNameHeaderLabel =
    providerRouteScope === "doctor"
      ? "Doctor"
      : providerRouteScope === "hospital"
        ? "Hospital"
        : "Doctor / Hospital";

  const providerEntityLabels = useMemo(() => {
    if (isDoctorListRoute) {
      return { singular: "doctor", plural: "doctors" };
    }
    if (isHospitalListRoute) {
      return { singular: "hospital", plural: "hospitals" };
    }
    return { singular: "provider", plural: "providers" };
  }, [isDoctorListRoute, isHospitalListRoute]);

  const providerRangeText = isMobilePagination
    ? providerTotal > 0
      ? `Showing ${displayProviderList.length} of ${providerTotal} ${
          providerTotal === 1 ? providerEntityLabels.singular : providerEntityLabels.plural
        }`
      : `No ${providerEntityLabels.plural}`
    : formatShowingRange({
        ...getPageRange({
          currentPage: providerDesktopPage,
          pageSize: providerPageSize,
          totalItems: providerTotal,
          displayedCount: displayProviderList.length,
        }),
        total: providerTotal,
        entityPlural: providerEntityLabels.plural,
        entitySingular: providerEntityLabels.singular,
      });

  const patientRangeText = isMobilePagination
    ? patientTotal > 0
      ? `Showing ${displayPatientList.length} of ${patientTotal} ${
          patientTotal === 1 ? "patient" : "patients"
        }`
      : "No patients"
    : formatShowingRange({
        ...getPageRange({
          currentPage: patientDesktopPage,
          pageSize: patientPageSize,
          totalItems: patientTotal,
          displayedCount: displayPatientList.length,
        }),
        total: patientTotal,
        entityPlural: "patients",
        entitySingular: "patient",
      });

  return (
    <AdminLayout>
      <div className={`new_signups_page${isSplitListRoute ? " new_signups_page--fill" : ""}`}>
        {!isSplitListRoute && (
          <div className="new_signups_dual_header">
            <button
              type="button"
              className={`new_signups_tab${listType === LIST_TYPE.PATIENT ? " active" : ""}`}
              onClick={() => setListType(LIST_TYPE.PATIENT)}
            >
              Patient List
            </button>
            <button
              type="button"
              className={`new_signups_tab${listType === LIST_TYPE.PROVIDER ? " active" : ""}`}
              onClick={() => setListType(LIST_TYPE.PROVIDER)}
            >
              Doctors / Hospitals
            </button>
          </div>
        )}

        <div className={`new_signups_body_search${bodySearchOpen ? " open" : ""}`}>
          <input
            ref={bodySearchInputRef}
            type="text"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            placeholder={
              effectiveListType === LIST_TYPE.PATIENT
                ? "Search patients by name or mobile"
                : "Search doctors/hospitals by name or mobile"
            }
            className="new_signups_search_input"
          />
        </div>

        {mobileFilterOpen && (
          <div
            className="ns_overlay ns_mobile_filter_overlay"
            onClick={() => setMobileFilterOpen(false)}
            role="dialog"
            aria-modal="true"
          >
            <div className="ns_modal ns_mobile_filter_modal" onClick={(e) => e.stopPropagation()}>
              <div className="ns_modal_head">
                <h3>Filter {effectiveListType === LIST_TYPE.PATIENT ? "Patients" : "Doctors / Hospitals"}</h3>
                <button
                  type="button"
                  className="ns_close"
                  onClick={() => setMobileFilterOpen(false)}
                >
                  ×
                </button>
              </div>
              <div className="ns_modal_body">
                <div className="ns_mobile_filter_list">
                  {(effectiveListType === LIST_TYPE.PATIENT ? PATIENT_STATUS_FILTERS : STATUS_FILTERS).map((f) => {
                    const isActive =
                      effectiveListType === LIST_TYPE.PATIENT
                        ? patientStatusFilter === f.value
                        : statusFilter === f.value;
                    return (
                      <button
                        key={f.value}
                        type="button"
                        className={`ns_mobile_filter_option${isActive ? " active" : ""}`}
                        onClick={() => {
                          if (effectiveListType === LIST_TYPE.PATIENT) setPatientStatusFilter(f.value);
                          else setStatusFilter(f.value);
                          setMobileFilterOpen(false);
                        }}
                      >
                        {f.label}
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>
        )}
        {highlightId && (
          <div className="new_signups_highlight_banner">
            <span>Showing search result.</span>
            <button type="button" className="new_signups_show_all_btn" onClick={() => setSearchParams((p) => { const next = new URLSearchParams(p); next.delete("highlight"); return next; }, { replace: true })}>
              Show all
            </button>
          </div>
        )}
        <div className="new_signups_table_wrap" ref={tableWrapRef}>
          <div className="new_signups_table_inner" ref={tableScrollRef}>
          {effectiveListType === LIST_TYPE.PATIENT ? (
            <table className="new_signups_table new_signups_table_patient">
              <thead>
                <tr>
                  <th>Patient</th>
                  <th>Date Created</th>
                  <th>Location</th>
                  <th>View</th>
                  <th>Delete</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <>
                    {Array.from({ length: 6 }).map((_, idx) => (
                      <tr key={`patient-skeleton-${idx}`}>
                        <td colSpan={5} className="empty_row">
                          <SkeletonBlock height={18} width="100%" />
                        </td>
                      </tr>
                    ))}
                  </>
                ) : displayPatientList.length === 0 ? (
                  <tr><td colSpan={5} className="empty_row">{highlightId ? "Patient not found." : "No patients found"}</td></tr>
                ) : (
                  displayPatientList.map((row) => {
                    const pid = getPatientId(row);
                    return (
                    <tr key={row.patientId || row._id} data-row-id={`patient-${row.patientId || row._id}`}>
                      <td className="name_cell">
                        <AvatarImage
                          src={row.avatar || row.image}
                          alt={getPatientDisplayName(row)}
                          name={getPatientDisplayName(row)}
                          className="avatar_img"
                          fallbackClassName="avatar_placeholder"
                        />
                        <div className="name_and_title">
                          <span className="name_text">{getPatientDisplayName(row)}</span>
                          <span className="name_sub">Patient</span>
                        </div>
                      </td>
                      <td>{row.dateCreatedFormatted || row.dateCreated || "—"}</td>
                      <td>{row.location || "—"}</td>
                      <td>
                        <button
                          type="button"
                          className="btn_view"
                          onClick={() => {
                            const pid = getPatientId(row);
                            if (pid) navigate(`/patient-list/${pid}`);
                          }}
                        >
                          View
                        </button>
                      </td>
                      <td>
                        <button
                          type="button"
                          className="btn_delete_icon"
                          title="Delete record"
                          disabled={!pid || actionLoading.delete === pid}
                          onClick={() => deletePatient(pid)}
                        >
                          {actionLoading.delete === pid ? "…" : <TrashIcon />}
                        </button>
                      </td>
                    </tr>
                  );
                  })
                )}
              </tbody>
            </table>
          ) : (
          <table className="new_signups_table">
            <thead>
              <tr>
                <th>{providerNameHeaderLabel}</th>
                <th>Date Created</th>
                <th>Status</th>
                <th>View</th>
                <th>Action</th>
                <th>Rating</th>
                <th>Restrict</th>
                <th>Delete</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <>
                  {Array.from({ length: 6 }).map((_, idx) => (
                    <tr key={`provider-skeleton-${idx}`}>
                      <td colSpan={8} className="empty_row">
                        <SkeletonBlock height={18} width="100%" />
                      </td>
                    </tr>
                  ))}
                </>
              ) : displayProviderList.length === 0 ? (
                <tr><td colSpan={8} className="empty_row">{highlightId ? "Record not found." : "No sign-ups found"}</td></tr>
              ) : (
                displayProviderList.map((row) => (
                  <tr key={`${row.type}-${row.id}`} data-row-id={`${row.type}-${row.id}`}>
                    <td className="name_cell">
                      <AvatarImage
                        src={row.image}
                        alt={row.name || "Doctor / Hospital"}
                        name={row.name || "Doctor / Hospital"}
                        className="avatar_img"
                        fallbackClassName="avatar_placeholder"
                      />
                      <div className="name_and_title">
                        <span className="name_text">{row.name}</span>
                        <span className="name_sub">{row.type === "doctor" ? "Doctor" : "Hospital"}</span>
                      </div>
                    </td>
                    <td>{row.dateCreated}</td>
                    <td>
                      <span className={`status_pill status_bullet status_${getStatusDisplay(row).bullet}`}>
                        <span className="status_dot" />
                        {getStatusDisplay(row).label}
                      </span>
                    </td>
                    <td>
                      <button type="button" className="btn_view" onClick={() => goToProviderDetail(row)}>View</button>
                    </td>
                    <td className="action_cell">
                      <button
                        type="button"
                        className="btn_approve btn_icon"
                        title="Accept"
                        disabled={row.status === "Approved" || actionLoading.approve === row.id}
                        onClick={() => approve(row.id, row.type)}
                      >
                        {actionLoading.approve === row.id ? "…" : <TickIcon />}
                      </button>
                      <button
                        type="button"
                        className="btn_reject btn_icon"
                        title="Reject"
                        disabled={row.status === "Rejected" || actionLoading.reject === row.id}
                        onClick={() => setRejectModal(row)}
                      >
                        {actionLoading.reject === row.id ? "…" : <XIcon />}
                      </button>
                    </td>
                    <td className="rating_cell">
                      <div className="rank_box rank_box--compact">
                        <button
                          type="button"
                          className="ns_rating_compact_row ns_rating_compact_row--action"
                          onClick={() => setPlatformRateModal(row)}
                          disabled={actionLoading.rate === row.id}
                          title="Platform (admin) rating — click to set or edit"
                        >
                          <span className="ns_rating_compact_val ns_rating_compact_val--platform">
                            {actionLoading.rate === row.id ? "…" : formatRatingScore(rating[row.id] ?? row.adminRating)}
                          </span>
                          <span className="ns_rating_compact_stars" aria-hidden>
                            <StarRatingDisplay rating={rating[row.id] ?? row.adminRating ?? null} />
                          </span>
                        </button>
                        <div
                          className="ns_rating_compact_row"
                          title={
                            row.patientRatingCount > 0
                              ? `${row.patientRatingCount} patient rating${row.patientRatingCount === 1 ? "" : "s"}`
                              : "No patient ratings yet"
                          }
                        >
                          <span className="ns_rating_compact_val ns_rating_compact_val--patient">
                            {formatRatingScore(row.patientRatingAverage)}
                          </span>
                          <span className="ns_rating_compact_stars" aria-hidden>
                            <StarRatingDisplay rating={row.patientRatingAverage ?? null} />
                          </span>
                        </div>
                      </div>
                    </td>
                    <td>
                      {isRestricted(row) ? (
                        <button
                          type="button"
                          className="btn_unrestrict"
                          disabled={actionLoading.restrict === row.id}
                          onClick={() => unrestrict(row)}
                        >
                          {actionLoading.restrict === row.id ? "…" : "Un-blacklist"}
                        </button>
                      ) : (
                        <button
                          type="button"
                          className="btn_restrict"
                          disabled={actionLoading.restrict === row.id}
                          onClick={() => setRestrictModal(row)}
                        >
                          Restrict
                        </button>
                      )}
                    </td>
                    <td>
                      <button
                        type="button"
                        className="btn_delete_icon"
                        title="Delete record"
                        disabled={actionLoading.delete === row.id}
                        onClick={() => deleteProvider(row.id, row.type)}
                      >
                        {actionLoading.delete === row.id ? "…" : <TrashIcon />}
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
          )}
          </div>
          {!highlightId && effectiveListType === LIST_TYPE.PROVIDER && (
            <div className="new_signups_table_footer">
              {isMobilePagination ? (
                <div className="ns_result_count ns_result_count--mobile">
                  <div className="ns_mobile_footer_row">
                    <span>{providerRangeText}</span>
                    <PageSizeSelect
                      value={providerPageSize}
                      disabled={loading || loadingMore}
                      className="ns_page_size_field--mobile"
                      onChange={handleProviderPageSizeChange}
                    />
                  </div>
                  {providerHasMore && !loadingMore ? (
                    <span className="ns_scroll_hint">Scroll down for more</span>
                  ) : null}
                  {loadingMore ? <span className="ns_scroll_hint">Loading more…</span> : null}
                </div>
              ) : (
                <ListPaginationBar
                  currentPage={providerDesktopPage}
                  totalPages={providerTotalPages}
                  totalItems={providerTotal}
                  pageSize={providerPageSize}
                  displayedCount={displayProviderList.length}
                  entityPlural={providerEntityLabels.plural}
                  entitySingular={providerEntityLabels.singular}
                  loading={loading}
                  onPageChange={goToProviderPage}
                  onPageSizeChange={handleProviderPageSizeChange}
                />
              )}
            </div>
          )}
          {!highlightId && effectiveListType === LIST_TYPE.PATIENT && (
            <div className="new_signups_table_footer">
              {isMobilePagination ? (
                <div className="ns_result_count ns_result_count--mobile">
                  <div className="ns_mobile_footer_row">
                    <span>{patientRangeText}</span>
                    <PageSizeSelect
                      value={patientPageSize}
                      disabled={loading || loadingMore}
                      className="ns_page_size_field--mobile"
                      onChange={handlePatientPageSizeChange}
                    />
                  </div>
                  {patientHasMore && !loadingMore ? (
                    <span className="ns_scroll_hint">Scroll down for more</span>
                  ) : null}
                  {loadingMore ? <span className="ns_scroll_hint">Loading more…</span> : null}
                </div>
              ) : (
                <ListPaginationBar
                  currentPage={patientDesktopPage}
                  totalPages={patientTotalPages}
                  totalItems={patientTotal}
                  pageSize={patientPageSize}
                  displayedCount={displayPatientList.length}
                  entityPlural="patients"
                  entitySingular="patient"
                  loading={loading}
                  onPageChange={goToPatientPage}
                  onPageSizeChange={handlePatientPageSizeChange}
                />
              )}
            </div>
          )}
        </div>
      </div>

      {platformRateModal && (
        <div
          className="ns_overlay"
          onClick={() => setPlatformRateModal(null)}
          role="dialog"
          aria-modal="true"
          aria-labelledby="ns-platform-rate-title"
        >
          <div className="ns_modal ns_platform_rate_modal" onClick={(e) => e.stopPropagation()}>
            <div className="ns_modal_head">
              <h3 id="ns-platform-rate-title">Platform rating – {platformRateModal.name || "—"}</h3>
              <button type="button" className="ns_close" onClick={() => setPlatformRateModal(null)}>
                ×
              </button>
            </div>
            <div className="ns_modal_body">
              <p className="ns_platform_rate_kind">
                {platformRateModal.type === "doctor" ? "Doctor" : "Hospital"}
              </p>
              {platformRateModal.status !== "Approved" ? (
                <p className="ns_platform_rate_warn">
                  Approve this provider first to set a platform rating.
                </p>
              ) : null}
              <div className="ns_modal_rating_split ns_modal_rating_split--compact">
                <div>
                  <p className="ns_rating_sub_label">Platform (admin)</p>
                  <div className="ns_rating_stars">
                    <StarRatingDisplay
                      rating={rating[platformRateModal.id] ?? platformRateModal.adminRating ?? null}
                    />
                  </div>
                  <p className="ns_rating_meta_line">
                    {(rating[platformRateModal.id] ?? platformRateModal.adminRating) != null ? (
                      <>
                        <strong>
                          {Number(
                            rating[platformRateModal.id] ?? platformRateModal.adminRating
                          ).toFixed(1)}
                        </strong>
                        <span className="ns_rating_out_of"> / 5</span>
                        <span className="ns_rating_sep"> · </span>
                        <span className="ns_rating_meta_muted">Admin score</span>
                      </>
                    ) : (
                      <span className="ns_rating_meta_muted">Not set yet</span>
                    )}
                  </p>
                </div>
                <div>
                  <p className="ns_rating_sub_label">Patients</p>
                  <div className="ns_rating_stars">
                    <StarRatingDisplay rating={platformRateModal.patientRatingAverage ?? null} />
                  </div>
                  {platformRateModal.patientRatingCount > 0 &&
                  platformRateModal.patientRatingAverage != null ? (
                    <p className="ns_rating_meta_line">
                      <strong>
                        {Number(platformRateModal.patientRatingAverage).toFixed(1)}
                      </strong>
                      <span className="ns_rating_out_of"> / 5</span>
                      <span className="ns_rating_sep"> · </span>
                      {platformRateModal.patientRatingCount} patient
                      {platformRateModal.patientRatingCount === 1 ? " rating" : " ratings"}
                    </p>
                  ) : (
                    <p className="ns_rating_meta_line ns_rating_meta_line--muted">
                      No patient ratings yet
                    </p>
                  )}
                </div>
              </div>
              <div className="ns_platform_rate_pick">
                <p className="ns_rating_sub_label">Set platform score</p>
                <p className="ns_rating_sub_hint">
                  Tap the stars to save (whole stars only). Patient scores above are from patients in the app.
                </p>
                <InteractiveStarRating
                  value={rating[platformRateModal.id] ?? platformRateModal.adminRating}
                  onChange={(v) =>
                    setRate(platformRateModal.id, platformRateModal.type, v)
                  }
                  disabled={platformRateModal.status !== "Approved"}
                  busy={actionLoading.rate === platformRateModal.id}
                />
              </div>
            </div>
          </div>
        </div>
      )}

      {restrictModal && (
        <div className="ns_overlay" onClick={() => { setRestrictModal(null); setRestrictReason(""); }} role="dialog" aria-modal="true">
          <div className="ns_modal ns_restrict_modal" onClick={(e) => e.stopPropagation()}>
            <div className="ns_modal_head">
              <h3>Restrict – {restrictModal.name}</h3>
              <button type="button" className="ns_close" onClick={() => { setRestrictModal(null); setRestrictReason(""); }}>×</button>
            </div>
            <div className="ns_modal_body">
              <p className="restrict_desc">Choose how to restrict this {restrictModal.type}. Lifetime = blacklist (cannot login or appear in match-making until un-blacklisted). X Days = restricted for that many days, then access is restored automatically.</p>
              <div className="restrict_reason">
                <label>
                  <span>Reason for restriction (required)</span>
                  <input
                    type="text"
                    value={restrictReason}
                    onChange={(e) => setRestrictReason(e.target.value)}
                    placeholder="e.g. Policy violation"
                    className="restrict_reason_input"
                    required
                    aria-required="true"
                  />
                </label>
              </div>
              <div className="restrict_options">
                <button
                  type="button"
                  className="restrict_opt restrict_lifetime"
                  onClick={() => restrict(restrictModal, "lifetime")}
                  disabled={actionLoading.restrict === restrictModal.id || !restrictReason.trim()}
                >
                  Lifetime
                </button>
                <div className="restrict_days">
                  <label>
                    <span>Restrict for</span>
                    <input
                      type="number"
                      min={1}
                      max={365}
                      value={restrictDays}
                      onChange={(e) => setRestrictDays(Math.max(1, Math.min(365, Number(e.target.value) || 1)))}
                      className="restrict_days_input"
                    />
                    <span>days</span>
                  </label>
                  <button
                    type="button"
                    className="restrict_opt"
                    onClick={() => restrict(restrictModal, "days", restrictDays)}
                    disabled={actionLoading.restrict === restrictModal.id || !restrictReason.trim()}
                  >
                    X Days
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {rejectModal && (
        <div className="ns_overlay" onClick={() => { setRejectModal(null); setRejectionReason(""); }} role="dialog" aria-modal="true">
          <div className="ns_modal ns_reject_modal" onClick={(e) => e.stopPropagation()}>
            <div className="ns_modal_head">
              <h3>Reject – {rejectModal.name || "—"}</h3>
              <button type="button" className="ns_close" onClick={() => { setRejectModal(null); setRejectionReason(""); }}>×</button>
            </div>
            <div className="ns_modal_body">
              <p className="reject_desc">
                Provide a reason for rejecting. This reason is required and can be shown to them when they try to log in.
              </p>
              <div className="reject_reason">
                <label>
                  <span>Rejection reason</span>
                  <textarea
                    value={rejectionReason}
                    onChange={(e) => setRejectionReason(e.target.value)}
                    placeholder="e.g. Incomplete documents, invalid registration"
                    className="reject_reason_input"
                    rows={3}
                  />
                </label>
              </div>
              <div className="reject_actions">
                <button
                  type="button"
                  className="btn_reject"
                  disabled={
                    actionLoading.reject === (rejectModal.patientId || rejectModal.id) ||
                    !String(rejectionReason || "").trim()
                  }
                  onClick={submitRejectModal}
                >
                  {actionLoading.reject === (rejectModal.patientId || rejectModal.id) ? "…" : "Reject"}
                </button>
                <button
                  type="button"
                  className="btn_cancel"
                  onClick={() => { setRejectModal(null); setRejectionReason(""); }}
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </AdminLayout>
  );
}
